import { createFeature, createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { StoreItem, StoreItemEntity } from './store-item.model';
import { StoreItemActions } from './store-item.actions';
import * as _ from "lodash";

export const storeItemsFeatureKey = 'storeItems';


type StoreItemState = {
  [K in StoreItemEntity]: any[];
};

export interface State extends EntityState<StoreItem>, StoreItemState {
  // additional entities state properties
}

export const adapter: EntityAdapter<StoreItem> = createEntityAdapter<StoreItem>();

const storeItemInitialValues: Record<StoreItemEntity, any[]> = Object.values(StoreItemEntity).reduce(
  (acc, value) => {
    acc[value] = []; // Initialize an empty array for each enum value
    return acc;
  },
  {} as Record<StoreItemEntity, any[]>
);

export const initialState: State = adapter.getInitialState({
  // additional entity state properties
  ...storeItemInitialValues
});

export const reducer = createReducer(
  initialState,
  on(StoreItemActions.addStoreItem,
    (state, action) => adapter.addOne(action.storeItem, state)
  ),
  on(StoreItemActions.upsertStoreItem,
    (state, action) => {
      if (_.isArray(state[action?.entity])) {
        let items = [...state[action?.entity]]
        let index = items.findIndex((el: any) => el?.id === action?.storeItem?.id);
        if (index !== -1) {
          items[index] = action?.storeItem;
        } else {
          items.push(action?.storeItem);
        }

        let newState = { ...state }
        newState[action?.entity] = items;
        return newState;
      }

      return { ...state }
    }
  ),
  on(StoreItemActions.addStoreItems,
    (state, action) => adapter.addMany(action.storeItems, state)
  ),
  on(StoreItemActions.upsertStoreItems,
    (state, action) => adapter.upsertMany(action.storeItems, state)
  ),
  on(StoreItemActions.updateStoreItem,
    (state, action) => adapter.updateOne(action.storeItem, state)
  ),
  on(StoreItemActions.updateStoreItems,
    (state, action) => adapter.updateMany(action.storeItems, state)
  ),
  on(StoreItemActions.deleteStoreItem,
    (state, action) => {
      let items = [...state[action?.entity]];
      let index = items?.findIndex(i => i?.id === action?.id);
      items.splice(index, 1);

      let newState = { ...state }
      newState[action?.entity] = items;
      return newState;
    }
  ),
  on(StoreItemActions.deleteStoreItems,
    (state, action) => adapter.removeMany(action.ids, state)
  ),
  on(StoreItemActions.loadStoreItems,
    (state, action) => {
      if (_.isArray(state[action?.entity])) {
        let items: any[]
        items = action?.storeItems;
        let newState = { ...state }
        newState[action?.entity] = items;
        return newState;
      } else {
        let item: any
        item = action?.storeItems;
        let newState = { ...state }
        newState[action?.entity] = item;
        return newState;
      }
    }
  ),
  on(StoreItemActions.setPagination, (state, { pagination }) => ({
    ...state,
    pagination
  })),
  on(StoreItemActions.clearStoreItems,
    state => adapter.removeAll(state)
  ),
);

export const storeItemsFeature = createFeature({
  name: storeItemsFeatureKey,
  reducer,
  extraSelectors: ({ selectStoreItemsState }) => ({
    ...adapter.getSelectors(selectStoreItemsState)
  }),
});

export const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal,
} = adapter.getSelectors();

export const getAllStates = (state: State) => state;
